import { FC, useEffect, useState } from "react"
import { ConfigAdminToggleRow } from "../../../config-admin-form-primitives"
import { ConfigAdminPanel } from "../../../config-admin-panel"
import {
  RateCardsConfigDetailsData,
  RateCardsConfigTabOption
} from "../../../../../../types"
import TermLoanRow from "./TermLoanRow"

interface LoanTypePanelProps {
  group: "purchase" | "refinance"
  label: string
  loanTypes: { key: string; label: string }[]
  details: RateCardsConfigDetailsData
  setDetails: (details: RateCardsConfigDetailsData) => void
}

const LoanTypePanel: FC<LoanTypePanelProps> = ({
  group,
  label,
  loanTypes,
  details,
  setDetails
}) => {
  const tabs = details.tabs?.[group] ?? []
  const [groupEnabled, setGroupEnabled] = useState(tabs.length > 0)
  const [stashedTabs, setStashedTabs] = useState<RateCardsConfigTabOption[]>([])

  // Sync when tabs are updated externally (e.g. Formik reinitialize after save).
  // Safe for the "group ON, all terms off" case: toggling ON with an empty stash
  // leaves tabs.length at 0→0, so the effect never fires and groupEnabled stays true.
  useEffect(() => {
    setGroupEnabled(tabs.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length])

  const getTabOption = (loanType: string) =>
    tabs.find(option => option.type === loanType)

  const handleGroupToggle = (value: boolean) => {
    setGroupEnabled(value)
    if (!value) {
      setStashedTabs([...tabs])
      setDetails({ ...details, tabs: { ...details.tabs, [group]: [] } })
      return
    }
    setDetails({ ...details, tabs: { ...details.tabs, [group]: stashedTabs } })
    setStashedTabs([])
  }

  const handleLoanToggle = (
    loanType: string,
    defaultLabel: string,
    nextEnabled: boolean
  ) => {
    const filtered = details.tabs[group].filter(o => o.type !== loanType)
    const next = nextEnabled
      ? [...filtered, { type: loanType, label: defaultLabel }]
      : filtered
    setDetails({ ...details, tabs: { ...details.tabs, [group]: next } })
  }

  const handleLabelChange = (
    loanType: string,
    defaultLabel: string,
    nextLabel: string
  ) => {
    const next = details.tabs[group].map(o =>
      o.type === loanType
        ? { ...o, label: nextLabel.trim() || defaultLabel }
        : o
    )
    setDetails({ ...details, tabs: { ...details.tabs, [group]: next } })
  }

  if (loanTypes.length === 0) {
    return (
      <ConfigAdminPanel>
        <div className="px-4 py-4">
          <p className="text-sm font-medium text-gray-700">{label}</p>
          <p className="mt-1 text-sm text-gray-400">
            No {label.toLowerCase()} scenarios configured. Open Settings to add{" "}
            {label.toLowerCase()} scenario preferences.
          </p>
        </div>
      </ConfigAdminPanel>
    )
  }

  return (
    <ConfigAdminPanel>
      <ConfigAdminToggleRow
        label={label}
        description={`Control which ${label.toLowerCase()} loan types appear on the rate cards`}
        enabled={groupEnabled}
        onChange={handleGroupToggle}
        last={false}
      />
      {groupEnabled && (
        <div className="divide-y divide-gray-100 border-t border-gray-100">
          {loanTypes.map(lt => (
            <TermLoanRow
              key={lt.key}
              loanKey={lt.key}
              defaultLabel={lt.label}
              labelValue={getTabOption(lt.key)?.label ?? lt.label}
              enabled={Boolean(getTabOption(lt.key))}
              onLabelChange={v => handleLabelChange(lt.key, lt.label, v)}
              onEnabledChange={v => handleLoanToggle(lt.key, lt.label, v)}
            />
          ))}
        </div>
      )}
    </ConfigAdminPanel>
  )
}

export default LoanTypePanel
